import React, { useState, useEffect, useRef, useCallback } from 'react';
import { SecondaryBase } from './../Commons/SecondaryBase';
import { useFloatMessage, MESSAGE_TYPE } from '../../Providers/FloatMessage';
import * as DL from './../../../utilities/DL/Cronometer';
import {
    ConnectWebSocketForCronometerAdmin,
    DisconnectWebSocketForCronometerAdmin,
    ShowCover as WSShowCover,
    ShowGame as WSShowGame,
    ShowObjective as WSShowObjective,
    StartPhase as WSStartPhase,
    EndPhase as WSEndPhase,
    ChangePhase as WSChangePhase,
    ResetGame as WSResetGame
} from './../../../utilities/WS/Admin/CronometerAdminWS';

import { CronometerThemeModal } from './Cronometer/CronometerThemeModal';
import { CronometerDangerModal } from './Cronometer/CronometerDangerModal';

/**
 * Consola Central de Control Operativo y Monitor Analítico en Vivo para Cronómetro (Tile ID 50).
 * Integra:
 *  - Barra Superior de Proyección (Portada / Pantalla Juego / Temas)
 *  - Embudo de Conversión en Tiempo Real (9 KPIs estilo Óptimo Ópticas)
 *  - Monitor Analítico de Conexiones (Estilo Tiro a Gol: Activos, Entraron, Salieron, Registrados, Retención)
 *  - Controles Operativos de Fase (Z objetivo, Margen +- tolerancia, Modo Intermitente, Disparo y Fin de Fase)
 *  - Tabla de Fases Disputadas
 *  - Padrón de Participantes en Vivo con Búsqueda, Filtros Rápidos y Exportación Ejecutiva a Excel (ClosedXML)
 *
 * @component
 * @param {Object} props
 * @param {Function} props.getOutOfTheDynamic - Callback para regresar al Dashboard principal de administración.
 */
export function Cronometer({ getOutOfTheDynamic }) {
    const { showMessage } = useFloatMessage();

    // =========================================================================
    // ESTADOS: CONEXIÓN WEBSOCKET Y PROYECCIÓN
    // =========================================================================
    const [wsStatus, setWsStatus] = useState('idle'); // 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'error' | 'closed'
    const [projectedScreenMode, setProjectedScreenMode] = useState(null); // null | 'cover' | 'game'
    const [isThemeModalOpen, setIsThemeModalOpen] = useState(false);
    const [isResetModalOpen, setIsResetModalOpen] = useState(false);
    const [isExportingExcel, setIsExportingExcel] = useState(false);
    const [isDangerZoneOpen, setIsDangerZoneOpen] = useState(false);
    const [isDangerModalOpen, setIsDangerModalOpen] = useState(false);

    // =========================================================================
    // ESTADOS: SECCIÓN 1 - EMBUDO DE CONVERSIÓN (9 KPIS)
    // =========================================================================
    const [isLoadingFunnel, setIsLoadingFunnel] = useState(false);
    const [funnelStats, setFunnelStats] = useState({
        qrScan: 0,
        pageView: 0,
        formStart: 0,
        formComplete: 0,
        gameStart: 0,
        gameStopped: 0,
        classified: 0,
        eliminated: 0,
        disconnect: 0,
        totalParticipants: 0,
        totalWinners: 0,
        lastUpdatedLocal: ''
    });

    // =========================================================================
    // ESTADOS: SECCIÓN 2 - MONITOR ANALÍTICO DE CONEXIONES (ESTILO TIRO A GOL)
    // =========================================================================
    const [connectionStats, setConnectionStats] = useState({
        currentlyActive: 0,
        totalEverConnected: 0,
        totalLeft: 0,
        totalRegistered: 0,
        retentionRate: 0.0
    });

    // =========================================================================
    // ESTADOS: SECCIÓN 3 - CONTROLES DE FASE Y OPERACIÓN DE JUEGO
    // =========================================================================
    const [currentPhase, setCurrentPhase] = useState(1);
    const [targetZ, setTargetZ] = useState(9.0);
    const [marginTolerance, setMarginTolerance] = useState(0.10);
    const [isIntermittent, setIsIntermittent] = useState(false);
    const [isPhaseRunning, setIsPhaseRunning] = useState(false);
    const [phaseSelectedToChange, setPhaseSelectedToChange] = useState(1);
    const [disputedPhasesCount, setDisputedPhasesCount] = useState(1);

    // =========================================================================
    // ESTADOS: SECCIÓN 4 Y 5 - TABLAS Y PADRÓN DE PARTICIPANTES
    // =========================================================================
    const [participantsList, setParticipantsList] = useState([]);
    const [isLoadingRecords, setIsLoadingRecords] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeFilter, setActiveFilter] = useState(''); // '' (todos), 'abonados', 'online', 'clasificados', 'marketing'
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 20;

    const searchDebounceRef = useRef(null);

    // =========================================================================
    // CARGA DE DATOS: FUNNEL, CONEXIONES Y PADRÓN
    // =========================================================================

    const loadFunnelStats = useCallback(async () => {
        setIsLoadingFunnel(true);
        try {
            const response = await DL.GetAdminStats();
            if (response && response.success && response.data) {
                const d = response.data;
                const now = new Date();
                const localTimestamp = d.lastUpdatedLocal || `${now.toLocaleDateString('es-MX', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                })} ${now.toLocaleTimeString('es-MX', { hour12: false })}`;

                setFunnelStats({
                    qrScan: d.qrScan ?? 0,
                    pageView: d.pageView ?? 0,
                    formStart: d.formStart ?? 0,
                    formComplete: d.formComplete ?? 0,
                    gameStart: d.gameStart ?? 0,
                    gameStopped: d.gameStopped ?? 0,
                    classified: d.classified ?? 0,
                    eliminated: d.eliminated ?? 0,
                    disconnect: d.disconnect ?? 0,
                    totalParticipants: d.totalParticipants ?? 0,
                    totalWinners: d.totalWinners ?? 0,
                    lastUpdatedLocal: localTimestamp
                });
            }
        } catch (error) {
            console.error('Error al cargar embudo de Cronómetro:', error);
        } finally {
            setIsLoadingFunnel(false);
        }
    }, []);

    const loadConnectionStats = useCallback(async () => {
        try {
            const response = await DL.GetConnectionStats();
            if (response && response.success && response.data) {
                const s = response.data;
                setConnectionStats({
                    currentlyActive: s.currentlyActive ?? s.CurrentlyActive ?? 0,
                    totalEverConnected: s.totalEverConnected ?? s.TotalEverConnected ?? 0,
                    totalLeft: s.totalLeft ?? s.TotalLeft ?? 0,
                    totalRegistered: s.totalRegistered ?? s.TotalRegistered ?? 0,
                    retentionRate: s.retentionRate ?? s.RetentionRate ?? 0.0
                });
            }
        } catch (error) {
            console.error('Error al cargar estadísticas de conexión:', error);
        }
    }, []);

    const loadParticipants = useCallback(async (search = '', filter = '') => {
        setIsLoadingRecords(true);
        try {
            const response = await DL.GetAdminRecords(search, filter);
            if (response && response.success && response.data) {
                const d = response.data;
                const list = d.participants || (Array.isArray(d) ? d : []);
                setParticipantsList(list);
                if (d.disputedPhases !== undefined) {
                    setDisputedPhasesCount(Math.max(1, d.disputedPhases));
                }
            }
        } catch (error) {
            console.error('Error al cargar padrón de Cronómetro:', error);
        } finally {
            setIsLoadingRecords(false);
        }
    }, []);

    // Carga de configuración inicial del juego (Z y margen)
    const loadGameConfig = useCallback(async () => {
        try {
            const configRes = await DL.GetPublicConfig();
            if (configRes && configRes.success && configRes.data) {
                const c = configRes.data;
                if (c.targetZ !== undefined) setTargetZ(Number(c.targetZ));
                if (c.marginTolerance !== undefined) setMarginTolerance(Number(c.marginTolerance));
                if (c.isIntermittent !== undefined) setIsIntermittent(Boolean(c.isIntermittent));
                if (c.currentPhase !== undefined) {
                    setCurrentPhase(Number(c.currentPhase));
                    setPhaseSelectedToChange(Number(c.currentPhase));
                }
            }
        } catch (e) {
            console.error('Error al cargar configuración de juego:', e);
        }
    }, []);

    // =========================================================================
    // WEBSOCKET HANDLERS (EN TIEMPO REAL)
    // =========================================================================

    const handleIncomingWsMessage = useCallback((envelope) => {
        if (!envelope) return;
        const type = (envelope.type || envelope.Type || '').toLowerCase();
        const payload = envelope.payload !== undefined ? envelope.payload : (envelope.data !== undefined ? envelope.data : envelope);

        switch (type) {
            case 'crono_connection_stats': {
                if (payload) {
                    setConnectionStats({
                        currentlyActive: payload.currentlyActive ?? payload.CurrentlyActive ?? 0,
                        totalEverConnected: payload.totalEverConnected ?? payload.TotalEverConnected ?? 0,
                        totalLeft: payload.totalLeft ?? payload.TotalLeft ?? 0,
                        totalRegistered: payload.totalRegistered ?? payload.TotalRegistered ?? 0,
                        retentionRate: payload.retentionRate ?? payload.RetentionRate ?? 0.0
                    });
                }
                break;
            }

            case 'crono_change_phase': {
                const newPhase = Number(payload.currentPhase || payload.phase || payload.fase || 1);
                setCurrentPhase(newPhase);
                setPhaseSelectedToChange(newPhase);
                break;
            }

            case 'crono_game_state': {
                if (payload) {
                    if (payload.currentPhase !== undefined || payload.phase !== undefined) {
                        const p = Number(payload.currentPhase || payload.phase);
                        setCurrentPhase(p);
                        setPhaseSelectedToChange(p);
                    }
                    if (payload.targetZ !== undefined) setTargetZ(Number(payload.targetZ));
                    if (payload.marginTolerance !== undefined) setMarginTolerance(Number(payload.marginTolerance));
                    if (payload.isIntermittent !== undefined) setIsIntermittent(Boolean(payload.isIntermittent));
                    if (payload.status) {
                        setIsPhaseRunning(payload.status.toLowerCase() === 'playing');
                    } else if (payload.phaseState) {
                        setIsPhaseRunning(payload.phaseState.toLowerCase() === 'running');
                    }
                }
                break;
            }

            case 'crono_start_phase': {
                setIsPhaseRunning(true);
                if (payload && payload.fase !== undefined) {
                    setCurrentPhase(Number(payload.fase));
                    setPhaseSelectedToChange(Number(payload.fase));
                }
                showMessage({
                    type: MESSAGE_TYPE.INFO,
                    text: `¡Fase ${payload?.fase || currentPhase} en curso!`
                });
                break;
            }

            case 'crono_end_phase': {
                setIsPhaseRunning(false);
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: `Fase finalizada. Clasificados: ${payload?.classifiedCount ?? 0}, Eliminados: ${payload?.eliminatedCount ?? 0}`
                });
                loadFunnelStats();
                loadParticipants(searchTerm, activeFilter);
                break;
            }

            case 'crono_screen_mode': {
                if (payload && payload.mode) {
                    setProjectedScreenMode(payload.mode);
                }
                break;
            }

            case 'crono_reset_game':
            case 'crono_reset': {
                setCurrentPhase(1);
                setPhaseSelectedToChange(1);
                setIsPhaseRunning(false);
                loadFunnelStats();
                loadConnectionStats();
                loadParticipants('', '');
                showMessage({
                    type: MESSAGE_TYPE.INFO,
                    text: 'La dinámica ha sido reiniciada a Fase 1.'
                });
                break;
            }

            default:
                break;
        }
    }, [currentPhase, loadFunnelStats, loadConnectionStats, loadParticipants, searchTerm, activeFilter, showMessage]);

    const incomingWsHandlerRef = useRef(handleIncomingWsMessage);
    incomingWsHandlerRef.current = handleIncomingWsMessage;

    const stableWsMessageDispatcher = useCallback((envelope) => {
        if (incomingWsHandlerRef.current) {
            incomingWsHandlerRef.current(envelope);
        }
    }, []);

    const handleWsStatusChange = useCallback((status) => {
        setWsStatus(status);
    }, []);

    // Conectar WebSocket y sincronizar datos al montar (Ejecuta estrictamente una sola vez)
    useEffect(() => {
        loadGameConfig();
        loadFunnelStats();
        loadConnectionStats();
        loadParticipants('', '');

        ConnectWebSocketForCronometerAdmin(stableWsMessageDispatcher, handleWsStatusChange, 'superadmin');

        return () => {
            DisconnectWebSocketForCronometerAdmin();
        };
    }, [loadGameConfig, loadFunnelStats, loadConnectionStats, loadParticipants, stableWsMessageDispatcher, handleWsStatusChange]);

    // Búsqueda con debounce en el padrón
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        setCurrentPage(1);

        if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = setTimeout(() => {
            loadParticipants(value, activeFilter);
        }, 350);
    };

    const handleFilterChange = (filterKey) => {
        setActiveFilter(filterKey);
        setCurrentPage(1);
        loadParticipants(searchTerm, filterKey);
    };

    // =========================================================================
    // ACCIONES OPERATIVAS (COMANDOS ADMIN)
    // =========================================================================

    const handleShowCover = () => {
        WSShowCover();
        setProjectedScreenMode('cover');
        showMessage({
            type: MESSAGE_TYPE.SUCCESS,
            text: 'Portada promocional con código QR proyectada en pantalla gigante.'
        });
    };

    const handleShowGame = () => {
        WSShowGame();
        setProjectedScreenMode('game');
        showMessage({
            type: MESSAGE_TYPE.SUCCESS,
            text: 'Pantalla de juego activa proyectada en pantalla gigante.'
        });
    };

    const handleShowObjective = () => {
        const zNum = Number(targetZ) || 9.0;
        const marginNum = Number(marginTolerance) || 0.10;
        WSShowObjective(zNum, marginNum, isIntermittent);
        showMessage({
            type: MESSAGE_TYPE.INFO,
            text: `Objetivo revelado en pantalla: ${zNum.toFixed(2)} s ± ${marginNum.toFixed(2)} s ${isIntermittent ? '(Intermitente)' : ''}`
        });
    };

    const handleStartPhase = () => {
        const zNum = Number(targetZ) || 9.0;
        const marginNum = Number(marginTolerance) || 0.10;
        WSStartPhase(currentPhase, zNum, marginNum, isIntermittent);
        setIsPhaseRunning(true);
        showMessage({
            type: MESSAGE_TYPE.SUCCESS,
            text: `¡Lanzando cuenta regresiva para Fase ${currentPhase}!`
        });
    };

    const handleEndPhase = () => {
        WSEndPhase();
        setIsPhaseRunning(false);
        showMessage({
            type: MESSAGE_TYPE.INFO,
            text: `Fase ${currentPhase} finalizada. Haz clic en "Avanzar a Fase ${currentPhase + 1}" para continuar con los clasificados.`
        });
        setTimeout(() => {
            loadFunnelStats();
            loadParticipants(searchTerm, activeFilter);
        }, 1200);
    };

    const handleChangePhase = () => {
        const newPhase = Number(phaseSelectedToChange);
        if (newPhase <= 0) return;
        WSChangePhase(newPhase);
        setCurrentPhase(newPhase);
        showMessage({
            type: MESSAGE_TYPE.SUCCESS,
            text: `Puntero de fase cambiado a Fase ${newPhase}.`
        });
    };

    const handleNextPhase = () => {
        const next = currentPhase + 1;
        setPhaseSelectedToChange(next);
        WSChangePhase(next);
        setCurrentPhase(next);
        showMessage({
            type: MESSAGE_TYPE.SUCCESS,
            text: `¡Puntero avanzado a Fase ${next}! Modifica el objetivo si lo deseas y pulsa 'INICIAR FASE ${next}'.`
        });
    };

    const handleConfirmReset = async () => {
        try {
            showMessage({
                type: MESSAGE_TYPE.INFO,
                text: 'Reiniciando partida y estado en tiempo real...'
            });
            WSResetGame();
            await DL.ResetGame();
            setIsResetModalOpen(false);
            setCurrentPhase(1);
            setPhaseSelectedToChange(1);
            setIsPhaseRunning(false);
            await Promise.all([loadFunnelStats(), loadConnectionStats(), loadParticipants('', '')]);
            showMessage({
                type: MESSAGE_TYPE.SUCCESS,
                text: 'Partida reiniciada exitosamente a Fase 1.'
            });
        } catch (error) {
            console.error('Error al reiniciar partida:', error);
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error al reiniciar la partida.'
            });
        }
    };

    const handleExportExcel = async () => {
        setIsExportingExcel(true);
        try {
            showMessage({
                type: MESSAGE_TYPE.INFO,
                text: 'Compilando libro de cálculo oficial en ClosedXML...'
            });
            const res = await DL.ExportExcelReport();
            if (res && res.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: '¡Reporte en Excel descargado exitosamente!'
                });
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: res?.message || 'Error al descargar el archivo de Excel.'
                });
            }
        } catch (error) {
            console.error('Error al exportar a Excel:', error);
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de conexión al exportar reporte.'
            });
        } finally {
            setIsExportingExcel(false);
        }
    };

    const handleReturnToDashboard = () => {
        DisconnectWebSocketForCronometerAdmin();
        if (getOutOfTheDynamic && typeof getOutOfTheDynamic === 'function') {
            getOutOfTheDynamic();
        }
    };

    // Paginación del padrón
    const totalPages = Math.ceil(participantsList.length / itemsPerPage) || 1;
    const paginatedParticipants = participantsList.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    // =========================================================================
    // CÁLCULO DE TABLA DE FASES DISPUTADAS
    // =========================================================================
    const maxPhasesToRender = Math.max(currentPhase, disputedPhasesCount, 1);
    const phasesArray = Array.from({ length: maxPhasesToRender }, (_, i) => i + 1);

    return (
        <SecondaryBase actionToExecute={handleReturnToDashboard}>
            <div className="AdmCronometer container-fluid px-3 px-md-4 py-3">
                {/* ================================================================= */}
                {/* BARRA SUPERIOR DE PROYECCIÓN Y ESTADO                             */}
                {/* ================================================================= */}
                <div className="AdmCronometer__ProjectionBar d-flex justify-content-between align-items-center flex-wrap gap-3">
                    <div className="d-flex align-items-center gap-3">
                        <div
                            className="bg-danger text-white rounded-3 p-2 d-flex align-items-center justify-content-center shadow-sm"
                            style={{ width: '44px', height: '44px' }}
                        >
                            <i className="bi bi-stopwatch fs-4"></i>
                        </div>
                        <div>
                            <div className="d-flex align-items-center gap-2">
                                <h4 className="fw-black mb-0 text-white">Cronómetro</h4>
                            </div>
                            <div className="AdmCronometer__ProjectionBar__Status mt-1 d-flex align-items-center gap-2">
                                <span
                                    className={`status-dot ${
                                        wsStatus === 'connected'
                                            ? ''
                                            : wsStatus === 'reconnecting' || wsStatus === 'connecting'
                                            ? 'status-dot--reconnecting'
                                            : 'status-dot--error'
                                    }`}
                                ></span>
                                <span className={`badge ${wsStatus === 'connected' ? 'bg-success' : 'bg-secondary'} px-2 py-1`}>
                                    {wsStatus === 'connected' ? 'Conectado' : 'Desconectado'}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Acciones de Proyección */}
                    <div className="AdmCronometer__ProjectionBar__Actions">
                        <button
                            type="button"
                            className={`btn fw-bold px-3 py-2 shadow-sm d-flex align-items-center gap-2 ${
                                projectedScreenMode === 'cover' ? 'btn-light text-dark' : 'btn-outline-light'
                            }`}
                            onClick={handleShowCover}
                        >
                            <i className="bi bi-qr-code fs-6"></i>
                            <span>Mostrar Portada</span>
                        </button>

                        <button
                            type="button"
                            className={`btn fw-bold px-3 py-2 shadow-sm d-flex align-items-center gap-2 ${
                                projectedScreenMode === 'game' ? 'btn-light text-dark' : 'btn-outline-light'
                            }`}
                            onClick={handleShowGame}
                        >
                            <i className="bi bi-display fs-6"></i>
                            <span>Proyectar Pantalla de Juego</span>
                        </button>

                        <button
                            type="button"
                            className="btn btn-danger fw-bold px-3 py-2 shadow-sm d-flex align-items-center gap-2"
                            onClick={() => setIsThemeModalOpen(true)}
                        >
                            <i className="bi bi-palette-fill fs-6"></i>
                            <span>Gestionar Temas</span>
                        </button>
                    </div>
                </div>

                {/* ================================================================= */}
                {/* SECCIÓN 1: EMBUDO DE CONVERSIÓN EN TIEMPO REAL (9 KPIS)           */}
                {/* ================================================================= */}
                <div className="AdmCronometer__Funnel">
                    <p className="AdmCronometer__Funnel__Title">Embudo de conversión</p>
                    <div className="text-center mb-3">
                        <button
                            type="button"
                            className="btn btn-outline-primary btn-sm fw-bold d-inline-flex align-items-center gap-1 shadow-sm px-3 py-1.5"
                            onClick={() => {
                                showMessage({
                                    type: MESSAGE_TYPE.INFO,
                                    text: 'Actualizando métricas en tiempo real...'
                                });
                                loadFunnelStats();
                            }}
                            disabled={isLoadingFunnel}
                        >
                            <i
                                className={`bi bi-arrow-clockwise me-1 ${
                                    isLoadingFunnel ? 'spinner-border spinner-border-sm' : ''
                                }`}
                            ></i>
                            <span>Actualizar datos</span>
                        </button>
                        {funnelStats.lastUpdatedLocal && (
                            <div className="text-muted small mt-1">
                                <i className="bi bi-clock-history me-1"></i>
                                Actualizado el {funnelStats.lastUpdatedLocal}
                            </div>
                        )}
                    </div>

                    <div className="AdmCronometer__Funnel__Scroll">
                        {/* 1. Escaneos QR */}
                        <div className="AdmCronometer__Funnel__Card">
                            <i className="bi bi-qr-code text-danger AdmCronometer__Funnel__Card__Icon"></i>
                            <div className="AdmCronometer__Funnel__Card__Label">1. Escaneos QR</div>
                            <div className="AdmCronometer__Funnel__Card__Value">{funnelStats.qrScan}</div>
                        </div>

                        {/* 2. Visitas Web */}
                        <div className="AdmCronometer__Funnel__Card">
                            <i className="bi bi-globe2 text-primary AdmCronometer__Funnel__Card__Icon"></i>
                            <div className="AdmCronometer__Funnel__Card__Label">2. Visitas Web</div>
                            <div className="AdmCronometer__Funnel__Card__Value">{funnelStats.pageView}</div>
                        </div>

                        {/* 3. Inicios Form */}
                        <div className="AdmCronometer__Funnel__Card">
                            <i className="bi bi-pencil-square text-warning AdmCronometer__Funnel__Card__Icon"></i>
                            <div className="AdmCronometer__Funnel__Card__Label">3. Inicios Form</div>
                            <div className="AdmCronometer__Funnel__Card__Value">{funnelStats.formStart}</div>
                        </div>

                        {/* 4. Registros OK */}
                        <div className="AdmCronometer__Funnel__Card">
                            <i className="bi bi-check-circle-fill text-success AdmCronometer__Funnel__Card__Icon"></i>
                            <div className="AdmCronometer__Funnel__Card__Label">4. Registros OK</div>
                            <div className="AdmCronometer__Funnel__Card__Value">{funnelStats.formComplete}</div>
                        </div>

                        {/* 5. Crono Iniciado */}
                        <div className="AdmCronometer__Funnel__Card">
                            <i className="bi bi-play-circle-fill text-primary AdmCronometer__Funnel__Card__Icon"></i>
                            <div className="AdmCronometer__Funnel__Card__Label">5. Crono Iniciado</div>
                            <div className="AdmCronometer__Funnel__Card__Value">{funnelStats.gameStart}</div>
                        </div>

                        {/* 6. Crono Detenido */}
                        <div className="AdmCronometer__Funnel__Card">
                            <i className="bi bi-hand-index-thumb-fill text-info AdmCronometer__Funnel__Card__Icon"></i>
                            <div className="AdmCronometer__Funnel__Card__Label">6. Crono Detenido</div>
                            <div className="AdmCronometer__Funnel__Card__Value">{funnelStats.gameStopped}</div>
                        </div>

                        {/* 7. Clasificados */}
                        <div className="AdmCronometer__Funnel__Card">
                            <i className="bi bi-trophy-fill text-success AdmCronometer__Funnel__Card__Icon"></i>
                            <div className="AdmCronometer__Funnel__Card__Label">7. Clasificados</div>
                            <div className="AdmCronometer__Funnel__Card__Value text-success">{funnelStats.classified}</div>
                        </div>

                        {/* 8. Eliminados */}
                        <div className="AdmCronometer__Funnel__Card">
                            <i className="bi bi-x-circle-fill text-danger AdmCronometer__Funnel__Card__Icon"></i>
                            <div className="AdmCronometer__Funnel__Card__Label">8. Eliminados</div>
                            <div className="AdmCronometer__Funnel__Card__Value text-danger">{funnelStats.eliminated}</div>
                        </div>

                        {/* 9. Desconexiones */}
                        <div className="AdmCronometer__Funnel__Card">
                            <i className="bi bi-wifi-off text-muted AdmCronometer__Funnel__Card__Icon"></i>
                            <div className="AdmCronometer__Funnel__Card__Label">9. Desconexiones</div>
                            <div className="AdmCronometer__Funnel__Card__Value text-muted">{funnelStats.disconnect}</div>
                        </div>
                    </div>
                </div>

                {/* ================================================================= */}
                {/* SECCIÓN 2: MONITOR ANALÍTICO DE CONEXIONES (ESTILO TIRO A GOL)    */}
                {/* ================================================================= */}
                <div className="AdmCronometer__Connections">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="fw-bold text-dark mb-0 text-uppercase" style={{ letterSpacing: '0.5px' }}>
                            <i className="bi bi-activity text-danger me-2"></i>
                            Monitor de Conexiones en Vivo
                        </h6>
                        <span className="small text-muted fw-semibold">
                            Tasa de Retención:{' '}
                            <strong className="text-dark fs-6">{connectionStats.retentionRate}%</strong>
                        </span>
                    </div>

                    <div className="AdmCronometer__Connections__Scroll">
                        {/* Activos */}
                        <div className="AdmCronometer__Connections__Card AdmCronometer__Connections__Card--active">
                            <div className="AdmCronometer__Connections__Card__Value">
                                {connectionStats.currentlyActive}
                            </div>
                            <div className="AdmCronometer__Connections__Card__Label">
                                🟢 JUGADORES AHORA
                            </div>
                        </div>

                        {/* Total Entraron */}
                        <div className="AdmCronometer__Connections__Card AdmCronometer__Connections__Card--total">
                            <div className="AdmCronometer__Connections__Card__Value">
                                {connectionStats.totalEverConnected}
                            </div>
                            <div className="AdmCronometer__Connections__Card__Label">
                                ⚪ TOTAL (Entraron)
                            </div>
                        </div>

                        {/* Salieron */}
                        <div className="AdmCronometer__Connections__Card AdmCronometer__Connections__Card--left">
                            <div className="AdmCronometer__Connections__Card__Value">
                                {connectionStats.totalLeft}
                            </div>
                            <div className="AdmCronometer__Connections__Card__Label">
                                🔴 SALIERON
                            </div>
                        </div>

                        {/* Registrados */}
                        <div className="AdmCronometer__Connections__Card AdmCronometer__Connections__Card--registered">
                            <div className="AdmCronometer__Connections__Card__Value">
                                {connectionStats.totalRegistered}
                            </div>
                            <div className="AdmCronometer__Connections__Card__Label">
                                🔵 REGISTRADOS
                            </div>
                        </div>
                    </div>
                </div>

                {/* ================================================================= */}
                {/* SECCIÓN 3: CONTROLES DE FASE Y OPERACIÓN DE JUEGO                */}
                {/* ================================================================= */}
                <div className="AdmCronometer__Controls">
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 pb-3 border-bottom mb-3">
                        <div className="d-flex align-items-center gap-3">
                            <div className="AdmCronometer__Controls__PhaseBadge">
                                <i className="bi bi-flag-fill text-warning"></i>
                                <span>FASE ACTUAL: {currentPhase}</span>
                            </div>

                            {/* Selector para cambiar fase */}
                            <div className="input-group input-group-sm" style={{ width: '160px' }}>
                                <select
                                    className="form-select form-select-sm fw-bold"
                                    value={phaseSelectedToChange}
                                    onChange={(e) => setPhaseSelectedToChange(Number(e.target.value))}
                                    disabled={isPhaseRunning}
                                >
                                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((f) => (
                                        <option key={f} value={f}>
                                            Fase {f}
                                        </option>
                                    ))}
                                </select>
                                <button
                                    type="button"
                                    className="btn btn-outline-dark btn-sm fw-bold"
                                    onClick={handleChangePhase}
                                    disabled={isPhaseRunning || phaseSelectedToChange === currentPhase}
                                    title="Cambiar puntero de fase"
                                >
                                    Fijar
                                </button>
                            </div>
                        </div>

                        {/* Estado operativo de la fase */}
                        <div>
                            {isPhaseRunning ? (
                                <span className="badge bg-success-subtle text-success fs-6 fw-bold px-3 py-2 border border-success d-flex align-items-center gap-1.5 animate__animated animate__pulse animate__infinite">
                                    <span className="spinner-grow spinner-grow-sm" role="status"></span>
                                    <span>FASE EN CURSO</span>
                                </span>
                            ) : (
                                <span className="badge bg-light text-secondary fs-6 fw-semibold px-3 py-2 border">
                                    <i className="bi bi-pause-circle me-1"></i> EN ESPERA DE DISPARO
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Inputs de Regulación Operativa */}
                    <div className="AdmCronometer__Controls__Inputs">
                        {/* Target Z */}
                        <div>
                            <label className="form-label small fw-bold text-secondary mb-1">
                                Tiempo Objetivo Z (segundos):
                            </label>
                            <div className="input-group" style={{ width: '150px' }}>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="1"
                                    max="60"
                                    className="form-control text-center fw-bold fs-5"
                                    value={targetZ}
                                    onChange={(e) => setTargetZ(parseFloat(e.target.value) || 9.0)}
                                    disabled={isPhaseRunning}
                                />
                                <span className="input-group-text fw-bold">s</span>
                            </div>
                        </div>

                        {/* Margen Tolerancia */}
                        <div>
                            <label className="form-label small fw-bold text-secondary mb-1">
                                Margen ± Tolerancia (segundos):
                            </label>
                            <div className="input-group" style={{ width: '160px' }}>
                                <span className="input-group-text fw-bold">±</span>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0.01"
                                    max="5"
                                    className="form-control text-center fw-bold fs-5"
                                    value={marginTolerance}
                                    onChange={(e) => setMarginTolerance(parseFloat(e.target.value) || 0.10)}
                                    disabled={isPhaseRunning}
                                />
                                <span className="input-group-text fw-bold">s</span>
                            </div>
                        </div>

                        {/* Switch Modo Intermitente */}
                        <div>
                            <label className="form-label small fw-bold text-secondary mb-1 d-block">
                                Visibilidad en Celular:
                            </label>
                            <div className="AdmCronometer__Controls__SwitchToggle">
                                <div className="form-check form-switch mb-0">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        role="switch"
                                        id="intermittentSwitch"
                                        checked={isIntermittent}
                                        onChange={(e) => setIsIntermittent(e.target.checked)}
                                        disabled={isPhaseRunning}
                                    />
                                    <label
                                        className="form-check-label fw-bold small text-dark cursor-pointer ms-1"
                                        htmlFor="intermittentSwitch"
                                    >
                                        Modo Intermitente {isIntermittent ? '⚡ (Blink Activo)' : '(Constante)'}
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Botonera de Acción Táctica */}
                    <div className="AdmCronometer__Controls__Buttons">
                        {/* 1. Mostrar Objetivo */}
                        <button
                            type="button"
                            className="btn btn-outline-primary"
                            onClick={handleShowObjective}
                            disabled={isPhaseRunning}
                        >
                            <i className="bi bi-eye-fill fs-5"></i>
                            <span>MOSTRAR OBJETIVO</span>
                        </button>

                        {/* 2. Iniciar Fase */}
                        <button
                            type="button"
                            className="btn btn-success"
                            onClick={handleStartPhase}
                            disabled={isPhaseRunning}
                        >
                            <i className="bi bi-play-circle-fill fs-5"></i>
                            <span>INICIAR FASE {currentPhase}</span>
                        </button>

                        {/* 3. Finalizar Fase y Avanzar Fase (Apilados verticalmente) */}
                        <div className="d-flex flex-column gap-2 justify-content-between">
                            <button
                                type="button"
                                className="btn btn-warning w-100"
                                onClick={handleEndPhase}
                                disabled={!isPhaseRunning}
                            >
                                <i className="bi bi-stop-circle-fill fs-5"></i>
                                <span>FINALIZAR FASE</span>
                            </button>

                            <button
                                type="button"
                                className="btn btn-outline-warning w-100 fw-bold d-flex align-items-center justify-content-center gap-1.5 shadow-sm"
                                onClick={handleNextPhase}
                                disabled={isPhaseRunning}
                                title={`Avanzar automáticamente el puntero a la Fase ${currentPhase + 1}`}
                            >
                                <i className="bi bi-fast-forward-fill fs-5"></i>
                                <span>AVANZAR A FASE {currentPhase + 1}</span>
                            </button>
                        </div>

                        {/* 4. Reiniciar Partida */}
                        <button
                            type="button"
                            className="btn btn-outline-danger"
                            onClick={() => setIsResetModalOpen(true)}
                            disabled={isPhaseRunning}
                        >
                            <i className="bi bi-arrow-counterclockwise fs-5"></i>
                            <span>REINICIAR PARTIDA</span>
                        </button>
                    </div>
                </div>

                {/* ================================================================= */}
                {/* SECCIÓN 4: TABLA DE FASES DISPUTADAS                              */}
                {/* ================================================================= */}
                <div className="AdmCronometer__Tables mb-4">
                    <div className="AdmCronometer__Tables__Card">
                        <h6 className="fw-bold text-dark mb-3 d-flex align-items-center gap-2">
                            <i className="bi bi-table text-danger"></i>
                            Resumen de Fases Disputadas
                        </h6>

                        <div className="AdmCronometer__Tables__TableWrapper">
                            <table className="table table-hover align-middle text-center">
                                <thead>
                                    <tr>
                                        <th>FASE</th>
                                        <th>CLASIFICADOS</th>
                                        <th>ESTADO</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {phasesArray.map((p) => {
                                        const countInPhase = participantsList.filter(
                                            (part) => part.phaseReached >= p && part.status !== 'ELIMINADO'
                                        ).length;

                                        let statusBadge;
                                        if (p < currentPhase) {
                                            statusBadge = (
                                                <span className="badge bg-secondary text-white px-2.5 py-1 fw-bold">
                                                    Finalizada
                                                </span>
                                            );
                                        } else if (p === currentPhase) {
                                            statusBadge = isPhaseRunning ? (
                                                <span className="badge bg-success text-white px-2.5 py-1 fw-bold animate__animated animate__pulse animate__infinite">
                                                    En Curso
                                                </span>
                                            ) : (
                                                <span className="badge bg-warning text-dark px-2.5 py-1 fw-bold">
                                                    En Espera
                                                </span>
                                            );
                                        } else {
                                            statusBadge = (
                                                <span className="badge bg-light text-muted px-2.5 py-1 fw-semibold border">
                                                    Pendiente
                                                </span>
                                            );
                                        }

                                        return (
                                            <tr key={p}>
                                                <td className="fw-bold text-dark">Fase {p}</td>
                                                <td className="fw-black fs-6 text-success">{countInPhase}</td>
                                                <td>{statusBadge}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* ================================================================= */}
                {/* SECCIÓN 5: PADRÓN DE PARTICIPANTES EN VIVO                        */}
                {/* ================================================================= */}
                <div className="AdmCronometer__Tables">
                    <div className="AdmCronometer__Tables__Card">
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3">
                            <div>
                                <h6 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
                                    <i className="bi bi-people-fill text-danger"></i>
                                    Padrón de Participantes en Vivo
                                </h6>
                                <span className="small text-muted">
                                    Total registrados: <strong>{participantsList.length}</strong> participantes
                                </span>
                            </div>

                            {/* Botón Exportar a Excel */}
                            <button
                                type="button"
                                className="btn btn-success fw-bold shadow-sm d-flex align-items-center gap-2 px-3 py-2"
                                onClick={handleExportExcel}
                                disabled={isExportingExcel}
                            >
                                {isExportingExcel ? (
                                    <>
                                        <div className="spinner-border spinner-border-sm" role="status"></div>
                                        <span>Exportando Excel...</span>
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-file-earmark-excel-fill fs-5"></i>
                                        <span>Exportar a Excel (.xlsx)</span>
                                    </>
                                )}
                            </button>
                        </div>

                        {/* Buscador y Filtros */}
                        <div className="row g-2 mb-3">
                            <div className="col-12 col-md-5">
                                <div className="input-group">
                                    <span className="input-group-text bg-white border-end-0">
                                        <i className="bi bi-search text-muted"></i>
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control border-start-0"
                                        placeholder="Buscar por nombre, correo o teléfono..."
                                        value={searchTerm}
                                        onChange={handleSearchChange}
                                    />
                                    {searchTerm && (
                                        <button
                                            className="btn btn-outline-secondary border-start-0"
                                            type="button"
                                            onClick={() => {
                                                setSearchTerm('');
                                                loadParticipants('', activeFilter);
                                            }}
                                        >
                                            <i className="bi bi-x"></i>
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className="col-12 col-md-7">
                                <div className="AdmCronometer__Tables__Filters">
                                    <button
                                        type="button"
                                        className={`btn-filter ${activeFilter === '' ? 'btn-filter--active' : ''}`}
                                        onClick={() => handleFilterChange('')}
                                    >
                                        Todos ({participantsList.length})
                                    </button>
                                    <button
                                        type="button"
                                        className={`btn-filter ${activeFilter === 'abonados' ? 'btn-filter--active' : ''}`}
                                        onClick={() => handleFilterChange('abonados')}
                                    >
                                        <i className="bi bi-ticket-perforated me-1"></i> Abonados
                                    </button>
                                    <button
                                        type="button"
                                        className={`btn-filter ${activeFilter === 'online' ? 'btn-filter--active' : ''}`}
                                        onClick={() => handleFilterChange('online')}
                                    >
                                        🟢 En Línea
                                    </button>
                                    <button
                                        type="button"
                                        className={`btn-filter ${activeFilter === 'clasificados' ? 'btn-filter--active' : ''}`}
                                        onClick={() => handleFilterChange('clasificados')}
                                    >
                                        🏆 Clasificados
                                    </button>
                                    <button
                                        type="button"
                                        className={`btn-filter ${activeFilter === 'marketing' ? 'btn-filter--active' : ''}`}
                                        onClick={() => handleFilterChange('marketing')}
                                    >
                                        📢 Consentimiento Publicidad
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Tabla */}
                        <div className="AdmCronometer__Tables__TableWrapper">
                            <table className="table table-hover align-middle">
                                <thead>
                                    <tr>
                                        <th style={{ width: '50px' }}>#</th>
                                        <th>NOMBRE</th>
                                        <th>WHATSAPP</th>
                                        <th>CORREO</th>
                                        <th>¿ABONADO?</th>
                                        <th>CONEXIÓN</th>
                                        <th>FASE</th>
                                        <th>PUNTAJE (s)</th>
                                        <th>RESULTADO</th>
                                        <th>HORA REGISTRO (CST)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isLoadingRecords ? (
                                        <tr>
                                            <td colSpan="10" className="text-center py-5">
                                                <div className="spinner-border text-danger mb-2" role="status"></div>
                                                <p className="text-muted small mb-0">Cargando registros de participantes...</p>
                                            </td>
                                        </tr>
                                    ) : paginatedParticipants.length === 0 ? (
                                        <tr>
                                            <td colSpan="10" className="text-center py-5 text-muted">
                                                <i className="bi bi-inbox fs-2 d-block mb-2"></i>
                                                <span>No se encontraron participantes con los filtros seleccionados.</span>
                                            </td>
                                        </tr>
                                    ) : (
                                        paginatedParticipants.map((p, idx) => {
                                            const globalIndex = (currentPage - 1) * itemsPerPage + idx + 1;
                                            const scoreText =
                                                p.finalTime && p.finalTime > 0
                                                    ? `${Number(p.finalTime).toFixed(2)} s`
                                                    : p.phaseTimes && p.phaseTimes[p.phaseReached]
                                                    ? `${Number(p.phaseTimes[p.phaseReached]).toFixed(2)} s`
                                                    : '-';

                                            return (
                                                <tr key={p.id || idx}>
                                                    <td className="fw-bold text-muted small">{globalIndex}</td>
                                                    <td className="fw-bold text-dark">{p.name}</td>
                                                    <td>
                                                        <a
                                                            href={`https://wa.me/52${p.phone}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-decoration-none text-success fw-semibold"
                                                        >
                                                            <i className="bi bi-whatsapp me-1"></i>
                                                            {p.phone}
                                                        </a>
                                                    </td>
                                                    <td className="text-secondary small">{p.email}</td>
                                                    <td>
                                                        {p.abonadoOption === 'Sí' || p.abonadoOption === 'si' ? (
                                                            <span className="badge bg-danger text-white">Abonado</span>
                                                        ) : (
                                                            <span className="badge bg-light text-secondary border">No</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {p.isOnline ? (
                                                            <span className="AdmCronometer__Badge AdmCronometer__Badge--online">
                                                                ● En Línea
                                                            </span>
                                                        ) : (
                                                            <span className="AdmCronometer__Badge AdmCronometer__Badge--offline">
                                                                ○ Desconectado
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="fw-bold text-center">Fase {p.phaseReached || 1}</td>
                                                    <td className="fw-mono text-center fw-bold">{scoreText}</td>
                                                    <td>
                                                        {p.isWinner ? (
                                                            <span className="AdmCronometer__Badge AdmCronometer__Badge--winner">
                                                                🥇 GANADOR
                                                            </span>
                                                        ) : p.status === 'CLASIFICADO' || p.phaseReached > 1 ? (
                                                            <span className="AdmCronometer__Badge AdmCronometer__Badge--classified">
                                                                CLASIFICADO
                                                            </span>
                                                        ) : (
                                                            <span className="AdmCronometer__Badge AdmCronometer__Badge--eliminated">
                                                                ELIMINADO
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="text-muted small">{p.createdAtLocal || '-'}</td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Paginación */}
                        {totalPages > 1 && (
                            <div className="d-flex justify-content-between align-items-center mt-3 pt-2 border-top">
                                <span className="small text-muted">
                                    Página <strong>{currentPage}</strong> de <strong>{totalPages}</strong> (
                                    {participantsList.length} participantes)
                                </span>
                                <div className="btn-group btn-group-sm">
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        disabled={currentPage === 1}
                                        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                    >
                                        Anterior
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        disabled={currentPage === totalPages}
                                        onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                                    >
                                        Siguiente
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* ===================================================================== */}
                {/* SECCIÓN PLEGABLE: ZONA PELIGROSA / REINICIAR INFORMACIÓN DE DINÁMICA  */}
                {/* ===================================================================== */}
                <div className="card border-0 rounded-4 shadow-sm bg-white mt-4 mb-4 overflow-hidden">
                    <div 
                        className="card-header bg-light border-0 py-3 px-4 d-flex justify-content-between align-items-center cursor-pointer"
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => setIsDangerZoneOpen(!isDangerZoneOpen)}
                    >
                        <h5 className="fw-bold mb-0 text-dark d-flex align-items-center gap-2">
                            <i className="bi bi-exclamation-triangle-fill text-danger fs-5"></i>
                            Zona Peligrosa: Reiniciar Información de la Dinámica
                        </h5>
                        <i className={`bi ${isDangerZoneOpen ? 'bi-chevron-up' : 'bi-chevron-down'} fs-5 text-secondary`}></i>
                    </div>
                    {isDangerZoneOpen && (
                        <div className="card-body p-4 border-top">
                            <div className="bg-danger-subtle p-3 rounded-3 border border-danger border-opacity-25">
                                <h6 className="fw-bold text-danger mb-2 d-flex align-items-center gap-2">
                                    <i className="bi bi-shield-fill-x fs-5"></i>
                                    Zona Peligrosa (Solo al finalizar o preparar nueva jornada)
                                </h6>
                                <p className="small text-danger-emphasis mb-3">
                                    Reiniciar la información de la dinámica archivará y eliminará permanentemente a todos los participantes registrados, tiempos de fases y métricas acumuladas, preparando el sistema desde cero para el siguiente partido. Esta acción es irreversible y requiere autorización con contraseña maestra.
                                </p>
                                <button
                                    type="button"
                                    onClick={() => setIsDangerModalOpen(true)}
                                    className="btn btn-danger w-100 py-2.5 fw-bold d-flex align-items-center justify-content-center gap-2 shadow-sm"
                                >
                                    <i className="bi bi-trash-fill"></i>
                                    Reiniciar dinámica por completo
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* ===================================================================== */}
            {/* MODAL DE TEMAS Y PATROCINADORES                                       */}
            {/* ===================================================================== */}
            <CronometerThemeModal
                isOpen={isThemeModalOpen}
                onClose={() => setIsThemeModalOpen(false)}
                onThemeActivated={() => {
                    loadGameConfig();
                }}
            />

            {/* ===================================================================== */}
            {/* MODAL DE ZONA PELIGROSA Y REINICIO CRÍTICO CON CONTRASEÑA MAESTRA    */}
            {/* ===================================================================== */}
            <CronometerDangerModal
                isOpen={isDangerModalOpen}
                onClose={() => setIsDangerModalOpen(false)}
                onPurgeSuccess={async () => {
                    setIsDangerModalOpen(false);
                    setCurrentPhase(1);
                    setPhaseSelectedToChange(1);
                    setIsPhaseRunning(false);
                    await Promise.all([loadFunnelStats(), loadConnectionStats(), loadParticipants('', '')]);
                }}
            />

            {/* ===================================================================== */}
            {/* MODAL DE CONFIRMACIÓN PARA REINICIAR PARTIDA                          */}
            {/* ===================================================================== */}
            {isResetModalOpen && (
                <div
                    className="modal fade show d-block"
                    tabIndex="-1"
                    style={{
                        backgroundColor: 'rgba(15, 23, 42, 0.75)',
                        backdropFilter: 'blur(5px)',
                        zIndex: 1070
                    }}
                >
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content shadow-lg border-0 rounded-4 overflow-hidden bg-white text-dark">
                            <div className="modal-header border-bottom bg-danger text-white px-4 py-3">
                                <h5 className="modal-title fw-bold d-flex align-items-center gap-2 mb-0">
                                    <i className="bi bi-arrow-counterclockwise"></i>
                                    Confirmar Reinicio de Partida
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    aria-label="Close"
                                    onClick={() => setIsResetModalOpen(false)}
                                ></button>
                            </div>
                            <div className="modal-body p-4 text-center">
                                <div className="text-danger mb-3">
                                    <i className="bi bi-arrow-counterclockwise fs-1"></i>
                                </div>
                                <h5 className="fw-bold text-dark mb-2">¿Estás seguro de reiniciar la partida actual?</h5>
                                <p className="text-muted small mb-0">
                                    Esta acción restablecerá el puntero del juego a la <strong>Fase 1</strong>,
                                    reiniciará los contadores en memoria RAM del servidor y notificará a las pantallas
                                    y celulares conectados para volver al estado inicial de juego.<br />
                                    <strong className="text-dark">No se borrarán los participantes registrados ni los datos de la base de datos.</strong>
                                </p>
                            </div>
                            <div className="modal-footer border-top bg-light px-4 py-3 d-flex justify-content-end gap-2">
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary fw-semibold px-4"
                                    onClick={() => setIsResetModalOpen(false)}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-danger fw-bold px-4 shadow-sm"
                                    onClick={handleConfirmReset}
                                >
                                    Sí, Reiniciar Partida
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </SecondaryBase>
    );
}

export default Cronometer;
